import { useState, useEffect, useRef } from 'preact/hooks';
import { getConfig } from '../config';
import { getSettings, setQnr, getOldQnr, buildFullQnr } from '../api';
import { store } from '../store';
import { navigateToView } from '../navigation';
import { SearchableSelect } from './SearchableSelect';
import { t } from '../i18n';
import { emit } from '../analytics';

const EMPTY_FORM = {
  bd_Day: '', bd_Month: '', bd_Year: '', p2: '', p3: '', p4: '',
  h201: '', h219: '', h220: '', h221: '', h222: '', h223: '',
};

type FormData = typeof EMPTY_FORM;

function oldQnrToFormData(oldData: Record<string, unknown>): FormData {
  const get = (key: string) => {
    const v = oldData[key];
    if (v === undefined || v === null) return '';
    return String(v).trim();
  };
  const pad2 = (s: string) => (s.length === 1 ? '0' + s : s);
  let bd_Day = pad2(get('bd_Day'));
  let bd_Month = pad2(get('bd_Month'));
  let bd_Year = get('bd_Year');
  const p1 = get('p1');
  if (!bd_Day && !bd_Month && !bd_Year && p1) {
    const parts = p1.replace(/\\\//g, '/').split('/');
    if (parts.length === 3) {
      bd_Day = pad2(parts[0]);
      bd_Month = pad2(parts[1]);
      bd_Year = parts[2];
    }
  }
  return {
    ...EMPTY_FORM,
    bd_Day, bd_Month, bd_Year,
    p2: get('p2'), p3: get('p3'), p4: get('p4'),
    h201: get('h201'), h219: get('h219'), h220: get('h220'),
    h221: get('h221'), h222: get('h222'), h223: get('h223'),
  };
}

export function CheckoutQnrPage() {
  const config = getConfig();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState<any>({});
  const [, setCartTotal] = useState(store.getCartTotal());
  const [, setTick] = useState(0);
  const formDataRef = useRef<FormData>(null!);
  if (!formDataRef.current) {
    const s = store.getOldQnrData();
    formDataRef.current = (s && typeof s === 'object' && Object.keys(s).length > 0) ? oldQnrToFormData(s) : { ...EMPTY_FORM };
  }
  const formData = formDataRef.current;
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const unsub = store.subscribe(() => setCartTotal(store.getCartTotal()));
    return unsub;
  }, []);

  const applyPayload = (payload: Record<string, unknown>) => {
    const next = oldQnrToFormData(payload);
    formDataRef.current = next;
    setTick((t) => t + 1);
  };

  useEffect(() => {
    const s = store.getOldQnrData();
    if (s && typeof s === 'object' && Object.keys(s).length > 0) {
      applyPayload(s);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      try {
        const [settingsRes, oldQnrRes] = await Promise.all([
          getSettings(config),
          getOldQnr(config)
        ]);
        if (cancelled) return;
        if (settingsRes.result) {
          setSettings(settingsRes.result);
          store.setSettings(settingsRes.result);
        }
        const raw = oldQnrRes as unknown as { result?: unknown; [k: string]: unknown };
        const payload = (raw?.result && typeof raw.result === 'object' && !Array.isArray(raw.result))
          ? (raw.result as Record<string, unknown>)
          : (typeof raw === 'object' && raw !== null && !Array.isArray(raw) && ('p1' in raw || 'bd_Day' in raw) ? raw : null);
        if (payload && !cancelled) {
          const hasServerQnr = Object.keys(payload).length > 0;
          const existing = store.getOldQnrData();
          const hasInStore = existing && typeof existing === 'object' && Object.keys(existing).length > 0;
          // После перезагрузки store пустой — берём QNR из сессии (getOldQnr). Иначе не затираем локальные/только что сохранённые данные.
          if (hasServerQnr && !hasInStore) {
            store.setOldQnrData(payload);
            applyPayload(payload);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[CheckoutQnrPage] loadData error:', err);
          setLoadError(err instanceof Error ? err.message : t('qnr.loadFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [config.apiUrl, config.siteKey]);

  const handleChange = (field: string, value: string) => {
    formDataRef.current = { ...formDataRef.current, [field]: value };
    store.setOldQnrData(buildFullQnr(formDataRef.current) as unknown as Record<string, unknown>);
    setTick((t) => t + 1);
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    if (submitError) setSubmitError(null);
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.bd_Day || !formData.bd_Month || !formData.bd_Year) newErrors.bd = t('qnr.dobRequired');
    if (!formData.p2) newErrors.p2 = t('qnr.genderRequired');
    if (!formData.p3) newErrors.p3 = t('qnr.heightRequired');
    if (!formData.p4) newErrors.p4 = t('qnr.weightRequired');
    const conditionLength = Array.from(formData.h201.trim()).length;
    if (conditionLength < 4 || conditionLength > 500) newErrors.h201 = t('qnr.conditionRequired');
    if (!formData.h219) newErrors.h219 = t('qnr.yesNo');
    if (formData.h219 === 'yes' && !formData.h220) newErrors.h220 = t('qnr.specifyRequired');
    if (!formData.h221) newErrors.h221 = t('qnr.declarationRequired');
    if (!formData.h222) newErrors.h222 = t('qnr.responsibilityRequired');
    if (!formData.h223) newErrors.h223 = t('qnr.consentRequired');

    setErrors(newErrors);
    if (Object.keys(newErrors).length) {
      emit('sw:checkout-validation-error', {
        step: 'questionnaire', error_count: Object.keys(newErrors).length, fields: Object.keys(newErrors),
      });
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!validate()) return;

    emit('sw:checkout-submit', { step: 'questionnaire' });
    setSubmitting(true);
    setSubmitError(null);
    const fullQnr = buildFullQnr(formData);
    const res = await setQnr(config, fullQnr);
    setSubmitting(false);

    if (res.status === 200) {
      store.setOldQnrData(fullQnr);
      store.addToast(t('qnr.saved'), 'success');
      navigateToView('checkout-confirm');
    } else {
      emit('sw:checkout-validation-error', { step: 'questionnaire', error_count: 1, fields: ['qnr'] });
      const errMsg = res.error || t('qnr.saveFailed');
      setSubmitError(errMsg);
      store.addToast(errMsg, 'error');
    }
  };

  if (loading && !loadError) {
    return (
      <div class="text-center py-5">
        <span class="spinner-border text-primary" role="status"></span>
        <p class="mt-2 text-muted">{t('qnr.loading')}</p>
      </div>
    );
  }

  if (loadError && !loading) {
    return (
      <div class="alert alert-danger d-flex align-items-center">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flex: '0 0 auto', marginRight: '0.5rem' }}>
          <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
          <path d="M12 7v6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          <circle cx="12" cy="16.5" r="1" fill="currentColor" />
        </svg>
        <span>{loadError}</span>
        <button class="btn btn-sm btn-outline-danger ms-auto" onClick={() => window.location.reload()}>{t('qnr.retry')}</button>
      </div>
    );
  }

  const currentYear = new Date().getFullYear();
  const minBirthYear = 1920;
  const dayOptions: Record<string, string> = {};
  for (let i = 1; i <= 31; i++) {
    const d = String(i).padStart(2, '0');
    dayOptions[d] = d;
  }
  const MONTH_NAMES = Array.from({ length: 12 }, (_, i) => t(`month.${i + 1}`));
  const monthOptions: Record<string, string> = {};
  for (let i = 0; i < 12; i++) {
    const m = String(i + 1).padStart(2, '0');
    monthOptions[m] = MONTH_NAMES[i];
  }
  const yearOptions: Record<string, string> = {};
  for (let y = currentYear; y >= minBirthYear; y--) yearOptions[String(y)] = String(y);

  const genderOptions = { male: 'Male', female: 'Female' };
  const heights = settings.HEIGHTS || { '150': '150 cm', '160': '160 cm', '170': '170 cm', '180': '180 cm' };
  const weights = settings.WEIGHTS || { '50': '50 kg', '60': '60 kg', '70': '70 kg', '80': '80 kg' };

  const YesNoSwitch = ({ name, value, onChange, label, error, confirm }: any) => (
    <div class={confirm ? 'sw-qnr__confirm' : 'sw-qnr__field'}>
      <div class={confirm ? 'sw-qnr__confirm-text' : undefined}>
        <label class={`form-label required d-block fw-bold${confirm ? ' mb-0' : ''}`}>{label}</label>
        {error && confirm ? <div class="small text-danger mt-2 d-block">{error}</div> : null}
      </div>
      <div class="btn-group sw-qnr__radios" role="group">
        <input
          type="radio"
          class="btn-check"
          name={name}
          id={`${name}_yes`}
          autocomplete="off"
          checked={value === 'yes'}
          onChange={() => onChange('yes')}
        />
        <label
          class={`btn btn-sm px-4 sw-qnr-toggle sw-qnr-toggle--yes${value === 'yes' ? ' is-selected' : ''}`}
          for={`${name}_yes`}
        >
          {t('qnr.yes')}
        </label>

        <input
          type="radio"
          class="btn-check"
          name={name}
          id={`${name}_no`}
          autocomplete="off"
          checked={value === 'no'}
          onChange={() => onChange('no')}
        />
        <label
          class={`btn btn-sm px-4 sw-qnr-toggle sw-qnr-toggle--no${value === 'no' ? ' is-selected' : ''}`}
          for={`${name}_no`}
        >
          {t('qnr.no')}
        </label>
      </div>
      {error && !confirm ? <div class="small text-danger mt-2 d-block">{error}</div> : null}
    </div>
  );

  return (
    <div class="shop-widget-checkout-qnr p-1">
      <h3 class="text-center mb-4 fw-bold text-warning">{t('qnr.title')}</h3>

      <div class="card mb-4 border shadow-sm overflow-hidden p-4 mt-2 mb-5">
        <div class="alert alert-warning small mb-4">
          {t('qnr.intro')}
        </div>

        {submitError && (
          <div class="alert alert-danger d-flex align-items-center mb-4" role="alert">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flex: '0 0 auto', marginRight: '0.5rem' }}>
              <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
              <path d="M12 7v6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
              <circle cx="12" cy="16.5" r="1" fill="currentColor" />
            </svg>
            <span>{submitError}</span>
          </div>
        )}

        <form class="sw-qnr" onSubmit={handleSubmit}>
          <section class="sw-qnr__section" aria-labelledby="sw-qnr-general">
            <h5 id="sw-qnr-general" class="sw-qnr__section-title">{t('qnr.general')}</h5>

            <div class={`sw-qnr__field ${errors.bd ? 'has-error' : ''}`}>
              <label class="form-label fw-bold required d-block mb-2">{t('qnr.dob')}</label>
              <div class="sw-qnr__dob">
                <div class="sw-qnr__dob-part">
                  <label class="sw-qnr__dob-label" for="bd_Day">{t('qnr.day')}</label>
                  <SearchableSelect
                    options={dayOptions}
                    value={formData.bd_Day}
                    onChange={(val) => handleChange('bd_Day', val)}
                    placeholder={t('qnr.day')}
                    name="bd_Day"
                    error={!!errors.bd}
                    searchable={false}
                  />
                </div>
                <div class="sw-qnr__dob-part">
                  <label class="sw-qnr__dob-label" for="bd_Month">{t('qnr.month')}</label>
                  <SearchableSelect
                    options={monthOptions}
                    value={formData.bd_Month}
                    onChange={(val) => handleChange('bd_Month', val)}
                    placeholder={t('qnr.month')}
                    name="bd_Month"
                    error={!!errors.bd}
                    searchable={false}
                  />
                </div>
                <div class="sw-qnr__dob-part">
                  <label class="sw-qnr__dob-label" for="bd_Year">{t('qnr.year')}</label>
                  <SearchableSelect
                    options={yearOptions}
                    value={formData.bd_Year}
                    onChange={(val) => handleChange('bd_Year', val)}
                    placeholder={t('qnr.year')}
                    name="bd_Year"
                    error={!!errors.bd}
                  />
                </div>
              </div>
              {errors.bd && <span class="form-error small text-danger mt-2 d-block">{errors.bd}</span>}
            </div>

            <div class="sw-qnr__grid">
              <div class="sw-qnr__field">
                <label class="form-label fw-bold required d-block mb-1">{t('qnr.gender')}</label>
                <SearchableSelect
                  options={genderOptions}
                  value={formData.p2}
                  onChange={(val) => handleChange('p2', val)}
                  placeholder={t('qnr.selectPlaceholder')}
                  name="qnr-p2"
                  error={!!errors.p2}
                  searchable={false}
                />
                {errors.p2 && <div class="small text-danger mt-2">{errors.p2}</div>}
              </div>
              <div class="sw-qnr__field">
                <label class="form-label fw-bold required d-block mb-1">{t('qnr.height')}</label>
                <SearchableSelect
                  options={heights}
                  value={formData.p3}
                  onChange={(val) => handleChange('p3', val)}
                  placeholder={t('qnr.selectPlaceholder')}
                  name="qnr-p3"
                  error={!!errors.p3}
                  searchable={false}
                />
                {errors.p3 && <div class="small text-danger mt-2">{errors.p3}</div>}
              </div>
              <div class="sw-qnr__field">
                <label class="form-label fw-bold required d-block mb-1">{t('qnr.weight')}</label>
                <SearchableSelect
                  options={weights}
                  value={formData.p4}
                  onChange={(val) => handleChange('p4', val)}
                  placeholder={t('qnr.selectPlaceholder')}
                  name="qnr-p4"
                  error={!!errors.p4}
                  searchable={false}
                />
                {errors.p4 && <div class="small text-danger mt-2">{errors.p4}</div>}
              </div>
            </div>
          </section>

          <section class="sw-qnr__section" aria-labelledby="sw-qnr-history">
            <h5 id="sw-qnr-history" class="sw-qnr__section-title">{t('qnr.medicalHistory')}</h5>

            <div class="sw-qnr__field">
              <label class="form-label fw-bold required">{t('qnr.disorderQuestion')}</label>
              <textarea
                class={`form-control ${errors.h201 ? 'is-invalid' : ''}`}
                rows={3}
                value={formData.h201}
                maxLength={500}
                onInput={(e) => handleChange('h201', (e.target as HTMLTextAreaElement).value)}
              ></textarea>
              {errors.h201 && <div class="small text-danger mt-2">{errors.h201}</div>}
            </div>

            <YesNoSwitch
              name="h219"
              value={formData.h219}
              onChange={(val: string) => handleChange('h219', val)}
              label={t('qnr.otherDisease')}
              error={errors.h219}
            />

            {formData.h219 === 'yes' && (
              <div class="sw-qnr__field">
                <label class="form-label fw-bold required">{t('qnr.specifyOther')}</label>
                <textarea
                  class={`form-control ${errors.h220 ? 'is-invalid' : ''}`}
                  rows={3}
                  value={formData.h220}
                  maxLength={500}
                  onInput={(e) => handleChange('h220', (e.target as HTMLTextAreaElement).value)}
                ></textarea>
                {errors.h220 && <div class="small text-danger mt-2">{errors.h220}</div>}
              </div>
            )}
          </section>

          <section class="sw-qnr__section" aria-labelledby="sw-qnr-confirm">
            <h5 id="sw-qnr-confirm" class="sw-qnr__section-title">{t('qnr.confirmations')}</h5>
            <div class="sw-qnr__confirms">
              <YesNoSwitch
                confirm
                name="h221"
                value={formData.h221}
                onChange={(val: string) => handleChange('h221', val)}
                label={t('qnr.declaration')}
                error={errors.h221}
              />
              <YesNoSwitch
                confirm
                name="h222"
                value={formData.h222}
                onChange={(val: string) => handleChange('h222', val)}
                label={t('qnr.responsibility')}
                error={errors.h222}
              />
              <YesNoSwitch
                confirm
                name="h223"
                value={formData.h223}
                onChange={(val: string) => handleChange('h223', val)}
                label={t('qnr.consent')}
                error={errors.h223}
              />
            </div>
          </section>

          <div class="sw-qnr__actions d-flex justify-content-between">
            <button type="button" class="btn btn-outline-secondary" onClick={() => navigateToView('checkout')}>{t('qnr.back')}</button>
            <button type="submit" class="btn btn-primary px-4" disabled={submitting}>
              {submitting ? t('qnr.saving') : t('qnr.continue')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
